import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { useRoleGuard } from '../../hooks/useRoleGuard';
import { Seo } from '../../components/seo/Seo';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  to: string;
  match: string;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Overview', to: '/dashboard/advertiser', match: '/dashboard/advertiser', end: true },
  {
    label: 'Campaigns',
    to: '/dashboard/advertiser/campaigns',
    match: '/dashboard/advertiser/campaigns',
  },
  {
    label: 'Reports',
    to: '/dashboard/advertiser/reports',
    match: '/dashboard/advertiser/reports',
  },
  {
    label: 'Account',
    to: '/dashboard/advertiser/account',
    match: '/dashboard/advertiser/account',
  },
];

function isSelected(currentPath: string, item: NavItem): boolean {
  if (item.end) return currentPath === item.match;
  return currentPath.startsWith(item.match);
}

/**
 * Advertiser dashboard shell — sidebar nav + main pane.
 *
 * Protected by `useRoleGuard('advertiser', 'admin')`. Signed-out users
 * are bounced to /login; signed-in users without the right role are
 * bounced to the home page with `?role-denied=1` so the public site
 * can surface a friendly message.
 */
export function AdvertiserLayout() {
  const { isAuthorized } = useRoleGuard('advertiser', 'admin');
  const location = useLocation();

  if (!isAuthorized) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Seo title="Advertiser portal" noindex />
      <Drawer
        variant="permanent"
        component="nav"
        aria-label="Advertiser dashboard sections"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            position: 'relative',
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 0, py: 2 }}>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Advertiser
          </Typography>
        </Toolbar>
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isSelected(location.pathname, item)}
              aria-current={isSelected(location.pathname, item) ? 'page' : undefined}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
      <Box
        component="section"
        sx={{ flex: 1, p: { xs: 2, md: 3 } }}
        aria-label="Advertiser dashboard content"
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdvertiserLayout;
